import type { UserSummary } from '@novachat/shared';
import { Check, Copy } from 'lucide-react';
import { Children, isValidElement, memo, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ThemedToken } from 'shiki/core';
import { useDirectory } from '../lib/directory';
import { grammarFor, tokenize } from '../lib/highlight';
import { mentionColors } from '../lib/avatar';
import { ProfileButton } from './ProfileCard';

/** Minimal mdast shapes for the mention plugin (no extra dependency for the full types). */
interface MdNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdNode[];
}

const MENTION = /<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>|(?<![\p{L}\p{N}_])@herkes(?![\p{L}\p{N}_])/giu;

/**
 * Turns `<@id>` (and @herkes, when the server counted it) into `mention:` links, only in plain
 * text: code spans and blocks keep their characters.
 */
function remarkMentions({ everyone }: { everyone: boolean }) {
  const split = (text: string): MdNode[] => {
    const out: MdNode[] = [];
    let last = 0;
    for (const match of text.matchAll(MENTION)) {
      const id = match[1]?.toLowerCase();
      if (!id && !everyone) continue;
      if (match.index > last) out.push({ type: 'text', value: text.slice(last, match.index) });
      out.push({ type: 'link', url: `mention:${id ?? 'everyone'}`, children: [{ type: 'text', value: match[0] }] });
      last = match.index + match[0].length;
    }
    if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
    return out;
  };
  const walk = (node: MdNode) => {
    if (!node.children || node.type === 'link') return;
    node.children = node.children.flatMap((child) => (child.type === 'text' && child.value ? split(child.value) : (walk(child), [child])));
  };
  return () => (tree: MdNode) => walk(tree);
}

/** Issue keys that have a card under the message (NOV-123) become links to Jira, outside code and links. */
function remarkJiraKeys(issues: readonly { key: string; url: string }[]) {
  const urls = new Map(issues.map((issue) => [issue.key, issue.url]));
  const pattern = new RegExp(`(?<![A-Za-z0-9_-])(${[...urls.keys()].join('|')})(?![A-Za-z0-9_])`, 'g');
  const split = (text: string): MdNode[] => {
    const out: MdNode[] = [];
    let last = 0;
    for (const match of text.matchAll(pattern)) {
      if (match.index > last) out.push({ type: 'text', value: text.slice(last, match.index) });
      out.push({ type: 'link', url: urls.get(match[1]!)!, children: [{ type: 'text', value: match[1]! }] });
      last = match.index + match[0].length;
    }
    if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
    return out;
  };
  const walk = (node: MdNode) => {
    if (!node.children || node.type === 'link') return;
    node.children = node.children.flatMap((child) => (child.type === 'text' && child.value ? split(child.value) : (walk(child), [child])));
  };
  return () => (tree: MdNode) => {
    if (urls.size > 0) walk(tree);
  };
}

const urlTransform = (url: string) => (url.startsWith('mention:') ? url : defaultUrlTransform(url));

function Mention({ id, people }: { id: string; people: readonly UserSummary[] }) {
  const person = people.find((p) => p.id === id);
  const look = 'rounded-[6px] bg-brand-400/20 px-1 py-px font-semibold text-brand-300';
  if (!person) return <span className={look}>@{id === 'everyone' ? 'herkes' : 'bilinmeyen'}</span>;
  // In the person's own avatar colour; inline, so the button's own classes cannot hide it.
  const { text, tint } = mentionColors(person.id);
  return (
    <ProfileButton user={person} className="rounded-[6px] px-1 py-px font-semibold hover:brightness-125">
      <span style={{ color: text, backgroundColor: tint }} className="-mx-1 -my-px rounded-[6px] px-1 py-px">
        @{person.name}
      </span>
    </ProfileButton>
  );
}

function CodeBlock({ code, lang }: { code: string; lang: string | undefined }) {
  const grammar = grammarFor(lang);
  const [lines, setLines] = useState<ThemedToken[][] | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!grammar) return;
    let current = true;
    tokenize(code, grammar)
      .then((tokens) => current && setLines(tokens))
      .catch(() => {}); // plain text it is
    return () => {
      current = false;
    };
  }, [code, grammar]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked: the text can still be selected by hand
    }
  }

  return (
    <div className="my-1.5 max-w-[720px] overflow-hidden rounded-md border border-line bg-surface-0">
      <div className="flex items-center justify-between gap-2 border-b border-line py-1 pr-1 pl-3">
        <span className="font-mono text-xs text-fg-3">{lang || 'kod'}</span>
        <button
          type="button"
          onClick={() => void copy()}
          aria-label={copied ? 'Kopyalandı' : 'Kodu kopyala'}
          className="flex h-7 items-center gap-1.5 rounded-xs border-0 bg-transparent px-2 text-xs font-semibold text-fg-2 hover:bg-surface-2 hover:text-fg-1"
        >
          {copied ? <Check size={13} className="text-green-400" aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
          {copied ? 'Kopyalandı' : 'Kopyala'}
        </button>
      </div>
      <pre className="m-0 max-h-[480px] overflow-auto px-3.5 py-3 font-mono text-[13px] leading-[1.6] whitespace-pre text-fg-body">
        <code>
          {lines
            ? lines.map((line, i) => (
                <span key={i}>
                  {line.map((token, j) => (
                    <span
                      key={j}
                      style={{
                        color: token.color,
                        fontStyle: (token.fontStyle ?? 0) & 1 ? 'italic' : undefined,
                        fontWeight: (token.fontStyle ?? 0) & 2 ? 700 : undefined,
                      }}
                    >
                      {token.content}
                    </span>
                  ))}
                  {i < lines.length - 1 ? '\n' : ''}
                </span>
              ))
            : code}
        </code>
      </pre>
    </div>
  );
}

const NO_ISSUES: readonly { key: string; url: string }[] = [];

const textOf = (node: ReactNode): string =>
  typeof node === 'string' ? node : Array.isArray(node) ? node.map(textOf).join('') : isValidElement(node) ? textOf((node.props as { children?: ReactNode }).children) : '';

function components(people: readonly UserSummary[]): Components {
  return {
    a: ({ href, children }) =>
      href?.startsWith('mention:') ? (
        <Mention id={href.slice('mention:'.length)} people={people} />
      ) : (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-teal-400 underline-offset-2 hover:underline">
          {children}
        </a>
      ),
    // Remote images stay links: they would load from anywhere (and the CSP blocks them anyway).
    img: ({ src, alt }) => (
      <a href={typeof src === 'string' ? src : undefined} target="_blank" rel="noopener noreferrer" className="text-teal-400 underline-offset-2 hover:underline">
        {alt || (typeof src === 'string' ? src : 'görsel')}
      </a>
    ),
    pre: ({ children }) => {
      const code = Children.toArray(children)[0] as ReactElement<{ className?: string; children?: ReactNode }> | undefined;
      const lang = /language-([\w+#-]+)/.exec(code?.props.className ?? '')?.[1];
      return <CodeBlock code={textOf(code?.props.children).replace(/\n$/, '')} lang={lang} />;
    },
    code: ({ children }) => <code className="rounded-xs bg-surface-3 px-1.5 py-0.5 font-mono text-[0.86em] text-fg-1">{children}</code>,
    p: ({ children }) => <p className="m-0">{children}</p>,
    ul: ({ children }) => <ul className="my-1 list-disc pl-5">{children}</ul>,
    ol: ({ children }) => <ol className="my-1 list-decimal pl-5">{children}</ol>,
    blockquote: ({ children }) => <blockquote className="my-1 border-l-[3px] border-line-strong pl-3 text-fg-2">{children}</blockquote>,
    h1: ({ children }) => <p className="mt-1 mb-0.5 font-heading text-lg font-bold text-fg-1">{children}</p>,
    h2: ({ children }) => <p className="mt-1 mb-0.5 font-heading text-base font-bold text-fg-1">{children}</p>,
    h3: ({ children }) => <p className="mt-1 mb-0.5 text-[15px] font-bold text-fg-1">{children}</p>,
    hr: () => <hr className="my-2 border-0 border-t border-line" />,
    table: ({ children }) => (
      <div className="my-1.5 max-w-full overflow-x-auto">
        <table className="border-collapse text-sm">{children}</table>
      </div>
    ),
    th: ({ children }) => <th className="border border-line bg-surface-2 px-2.5 py-1 text-left font-semibold">{children}</th>,
    td: ({ children }) => <td className="border border-line px-2.5 py-1">{children}</td>,
  };
}

/**
 * A message body: markdown without raw HTML (react-markdown never renders it), links open in a
 * new tab, mentions become chips.
 */
export const Markdown = memo(function Markdown({
  content,
  everyone,
  suffix,
  jira = NO_ISSUES,
}: {
  content: string;
  everyone: boolean;
  suffix?: ReactNode;
  /** Issues with a card under the message: their keys link to Jira. */
  jira?: readonly { key: string; url: string }[];
}) {
  const people = useDirectory();
  // Like Discord, ```kod``` on one line is a code block too (markdown would make it inline code).
  const source = useMemo(() => content.replace(/```([^`\n]+?)```/g, '\n```\n$1\n```\n'), [content]);
  return (
    // The "(düzenlendi)" note sits at the end of the last paragraph, not on a line of its own.
    <div className={`text-[15px] leading-normal [overflow-wrap:anywhere] text-fg-body [&>*+*]:mt-1 ${suffix ? '[&>p:nth-last-child(2)]:inline' : ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkMentions({ everyone }), remarkJiraKeys(jira)]} urlTransform={urlTransform} components={components(people)}>
        {source}
      </ReactMarkdown>
      {suffix}
    </div>
  );
});
